import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Globe, 
  Palette, 
  Terminal, 
  Lightbulb,
  Megaphone,
  Search,
  BarChart3,
  Shield,
  ArrowRight
} from 'lucide-react';
import SEO from '../../components/SEO';

const Services = () => {
  const { t } = useTranslation();

  const services = [
    { 
      icon: Globe, 
      title: t('services.webDevelopment.title'),
      description: t('services.webDevelopment.description'),
      path: '/thjonusta/vefsidugerd'
    },
    { 
      icon: Palette, 
      title: t('services.webDesign.title'),
      description: t('services.webDesign.description'),
      path: '/thjonusta/vefhonnun'
    },
    { 
      icon: Terminal, 
      title: t('services.webProgramming.title'),
      description: t('services.webProgramming.description'),
      path: '/thjonusta/vefthroun'
    },
    { 
      icon: Lightbulb, 
      title: t('services.consulting.title'),
      description: t('services.consulting.description'),
      path: '/thjonusta/radgjof'
    },
    { 
      icon: Megaphone, 
      title: t('services.marketing.title'),
      description: t('services.marketing.description'),
      path: '/thjonusta/markadssetning'
    },
    { 
      icon: Search, 
      title: t('services.seo.title'),
      description: t('services.seo.description'),
      path: '/thjonusta/leitarvelabestun'
    },
    { 
      icon: BarChart3, 
      title: t('services.analytics.title'),
      description: t('services.analytics.description'),
      path: '/thjonusta/greiningar'
    },
    { 
      icon: Shield, 
      title: t('services.maintenance.title'),
      description: t('services.maintenance.description'),
      path: '/thjonusta/vidhald'
    }
  ];

  return (
    <>
      <SEO 
        title={t('common.services')}
        description={t('services.description')}
        keywords={t('site.keywords')}
      />
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
            <motion.div 
              className="text-center max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl font-bold text-gray-900 mb-6">
                {t('services.title')}
              </h1>
              <p className="text-xl text-gray-600">
                {t('services.description')}
              </p>
            </motion.div>
          </div>
        </div>

        {/* Services Grid */}
        <div className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  className="bg-gray-50 rounded-xl p-8"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <Link to={service.path} className="block">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#EA295A]/10 mb-6">
                      <service.icon className="w-6 h-6 text-[#EA295A]" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">{service.title}</h3>
                    <p className="text-gray-600 mb-6">{service.description}</p>
                    <span className="inline-flex items-center text-[#EA295A] font-medium">
                      {t('common.readMore')}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-br from-[#EA295A] to-[#FF6B6B] rounded-2xl p-12 text-center">
              <motion.h2 
                className="text-3xl font-bold text-white mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                {t('services.cta.title')}
              </motion.h2>
              <motion.p 
                className="text-xl text-white/90 mb-8 max-w-2xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                {t('services.cta.description')}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <Link to="/fa-tilbod">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="bg-white text-[#EA295A] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all shadow-lg"
                  >
                    {t('common.getQuote')}
                  </motion.button>
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Services;